import { useCallback, useState } from 'react';
import { Mutators } from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';

import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';

import { IKnmErpFormRequirement } from '../interfaces/IKnmErpFormRequirement';
import styles from './KnmErpRequirement.module.scss';

const PAGE_SIZE = 10;

interface IKnmErpRequirementProps {
  remove: Mutators['remove'] | undefined;
  fieldName: string;
  data: IKnmErpFormRequirement;
  watchOnly?: boolean;
}

export const KnmErpRequirement = ({ remove, fieldName, data, watchOnly }: IKnmErpRequirementProps) => {
  const theadNameCellClassName = `${styles.Cell} ${styles.NameCell} ${styles.TheadCell}`;
  const theadPropsCellClassName = `${styles.Cell} ${styles.PropsCell} ${styles.TheadCell}`;
  const theadSmallCellClassName = `${styles.Cell} ${styles.SmallCell} ${styles.TheadCell}`;
  const tbodyNameCellClassName = `${styles.Cell} ${styles.NameCell} ${styles.TbodyCell}`;
  const tbodyPropsCellClassName = `${styles.Cell} ${styles.PropsCell} ${styles.TbodyCell}`;
  const tbodySmallCellClassName = `${styles.Cell} ${styles.SmallCell} ${styles.TbodyCell}`;

  const [lastVisibleRequirementNumber, setLastVisibleRequirementNumber] = useState<number>(PAGE_SIZE);
  const isNextPageAvailable = data?.documents && data.documents.length > lastVisibleRequirementNumber;

  const makeRemoveItemHandler = (fieldname: string, index: number) => () => {
    if (remove) {
      remove(fieldname, index);
    }
    setLastVisibleRequirementNumber(prev => prev - 1);
  };

  const handleLoadButtonClick = useCallback(() => setLastVisibleRequirementNumber(prev => prev + PAGE_SIZE), []);

  return (
    <>
      <div>
        <div className="shared-group-margin">
          <label className="shared-group-label" htmlFor={`${fieldName}.name`}>
            Наименование
          </label>
        </div>
        <TextInputField name={`${fieldName}.name`} placeholder="Наименование обязательного требования" disabled />
      </div>

      <div className={styles.Requirements}>
        <div className="shared-group-label">Требования</div>

        <table className={styles.Table}>
          <thead>
            <tr>
              <th className={theadNameCellClassName}>Формулировка требования</th>
              <th className={theadPropsCellClassName}>Наименование НПА</th>
              <th className={theadSmallCellClassName}>Номер НПА</th>
              <th className={theadSmallCellClassName}>Дата НПА</th>
              <th className={theadSmallCellClassName}>Часть</th>
              <th className={theadSmallCellClassName}>Пункт</th>
              <th className={theadSmallCellClassName}>Статья</th>
              <th className={theadSmallCellClassName} />
            </tr>
          </thead>
          <tbody>
            <FieldArray name={`${fieldName}.documents`}>
              {({ fields }) =>
                fields.map((name, index) => {
                  if (index >= lastVisibleRequirementNumber) {
                    return null;
                  }

                  return (
                    <tr className={styles.TbodyRow} key={name}>
                      <td className={tbodyPropsCellClassName}>{fields.value[index].props}</td>
                      <td className={tbodyNameCellClassName}>{fields.value[index].name}</td>
                      <td className={tbodySmallCellClassName}>{fields.value[index].number}</td>
                      <td className={tbodySmallCellClassName}>
                        {formatDate({ date: fields.value[index].date, dateFormat: DATE_FORMAT_LONG_FNS })}
                      </td>
                      <td className={tbodySmallCellClassName}>{fields.value[index].part}</td>
                      <td className={tbodySmallCellClassName}>{fields.value[index].paragraph}</td>
                      <td className={tbodySmallCellClassName}>{fields.value[index].article}</td>
                      <td className={tbodySmallCellClassName}>
                        <div className={styles.DeleteButtonBlock}>
                          <CloseButton
                            className={styles.DeleteButton}
                            disabled={watchOnly}
                            onClick={makeRemoveItemHandler(`${fieldName}.documents`, index)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              }
            </FieldArray>
          </tbody>
        </table>
        {isNextPageAvailable && (
          <Button type="button" text="Показать еще" color="load" onClick={handleLoadButtonClick} />
        )}
      </div>
    </>
  );
};
